import {
  Alert,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Skeleton,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute } from '@tanstack/react-router'
import { Ban, Clock, PackageCheck, ShoppingBag } from 'lucide-react'
import { useState } from 'react'

import { errorMessage } from '@/api/errors'
import {
  useCurrentCashSession,
  useCurrentPermissions,
  useLocations,
  useShopOrder,
  useShopOrderMutations,
  useShopOrders,
} from '@/api/queries'
import type { ShopOrderStatus, Uuid } from '@/api/types'
import { PageHeader } from '@/components/layout/app-shell'
import { formatDate, formatDateTime, formatMoney } from '@/lib/format'
import { notify } from '@/lib/toast'

/**
 * Gli ordini dal web — F8, SHOP-01.
 *
 * ## Un ordine non è una vendita, e la pagina lo dice
 *
 * Chi apre questa pagina ha una domanda sola: **cosa c'è da consegnare**. Per questo l'elenco parte
 * dagli ordini `placed` e li ordina **dal più vecchio** — l'opposto delle vendite, che si leggono
 * dall'ultima: là la domanda è «cosa è successo», qui «chi sta aspettando».
 *
 * ## I due gesti stanno sulla riga, e uno dei due chiede una sede
 *
 * «Ritira» e «Annulla» si fanno dove si legge il codice, perché chi ha la persona davanti al banco
 * non naviga. Il ritiro apre un modulo che chiede **solo ciò che serve a quel gesto**: la sede da cui
 * esce la merce e l'incasso. Senza sede il magazzino non si muove — è la regola di INV-03, non una
 * scelta di questa pagina — e la schermata lo dice invece di lasciarlo scoprire dopo.
 *
 * ## La scadenza si mostra, e in rosso quando è vicina
 *
 * Un ordine fermo da sei giorni è una telefonata da fare, ed è la ragione per cui il **recapito** sta
 * nell'elenco e non solo nel dettaglio: un elenco di codici obbligherebbe ad aprirli tutti per
 * trovare un numero.
 *
 * ## Il prezzo è quello dell'ordine, e non si corregge da qui
 *
 * Un ritiro non è una trattativa. Se il prezzo va cambiato si annulla l'ordine e si vende al banco,
 * dove la deroga sul prezzo esiste ed è tracciata: lasciarla qui avrebbe reso modificabile un numero
 * che il cliente ha già letto sul sito.
 */
export const Route = createFileRoute('/_app/ordini/')({
  component: ShopOrdersPage,
})

const STATUS_TABS: { key: ShopOrderStatus; label: string }[] = [
  { key: 'placed', label: 'Da consegnare' },
  { key: 'collected', label: 'Consegnati' },
  { key: 'cancelled', label: 'Annullati' },
  { key: 'expired', label: 'Scaduti' },
]

const STATUS_BADGE: Record<
  ShopOrderStatus,
  { label: string; variant: 'warning' | 'success' | 'neutral' | 'danger' }
> = {
  placed: { label: 'Da consegnare', variant: 'warning' },
  collected: { label: 'Consegnato', variant: 'success' },
  cancelled: { label: 'Annullato', variant: 'danger' },
  expired: { label: 'Scaduto', variant: 'neutral' },
}

/** Quanti giorni mancano alla scadenza. Negativo significa che è già passata. */
function daysLeft(expiresOn: string): number {
  const today = new Date()
  const target = new Date(`${expiresOn}T00:00:00Z`)
  const todayUtc = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate())
  return Math.round((target.getTime() - todayUtc) / 86_400_000)
}

function ShopOrdersPage() {
  const permissions = useCurrentPermissions()
  const canRead = permissions.has('sale:read')
  const canWrite = permissions.has('sale:write')
  const [status, setStatus] = useState<ShopOrderStatus>('placed')
  const [openOrderId, setOpenOrderId] = useState<Uuid | null>(null)
  const orders = useShopOrders(status)
  const rows = orders.data?.data ?? []

  if (!canRead) {
    return (
      <>
        <PageHeader title="Ordini dal sito" description="Cosa è stato ordinato e va consegnato." />
        <EmptyState
          icon={<ShoppingBag />}
          title="Non hai accesso agli ordini"
          description="Gli ordini dal sito richiedono il permesso «sale:read», lo stesso delle vendite: chi sta al banco è chi consegna."
        />
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Ordini dal sito"
        description="Cosa è stato ordinato e va consegnato. Il cliente paga al banco al momento del ritiro."
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.key}
            variant={status === tab.key ? 'primary' : 'outline'}
            size="sm"
            onClick={() => {
              setStatus(tab.key)
              setOpenOrderId(null)
            }}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {orders.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : orders.isError && orders.data === undefined ? (
        <ErrorState
          message={errorMessage(orders.error)}
          onRetry={() => {
            void orders.refetch()
          }}
        />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<ShoppingBag />}
          title={status === 'placed' ? 'Niente da consegnare' : 'Nessun ordine'}
          description={
            status === 'placed'
              ? 'Quando qualcuno ordina dalla vetrina del sito, l’ordine compare qui con il suo codice.'
              : 'Cambia scheda per vedere gli ordini negli altri stati.'
          }
        />
      ) : (
        <div className="border-border divide-border divide-y overflow-hidden rounded-lg border">
          {rows.map((row) => {
            const left = daysLeft(row.expiresOn)
            const expiring = row.status === 'placed' && left <= 2
            return (
              <div key={row.id}>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2 p-3">
                  {/* Il codice per primo e in monospazio: è ciò che il cliente dice a voce. */}
                  <span className="font-mono text-sm font-semibold">{row.code}</span>
                  <span className="min-w-40 flex-1 text-sm">
                    {row.customerFirstName} {row.customerLastName}
                    {row.customerPhone === null ? null : (
                      <span className="text-muted-foreground"> · {row.customerPhone}</span>
                    )}
                  </span>
                  <span className="text-sm tabular-nums">
                    {formatMoney(row.totalCents, row.currency)}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {row.itemCount === 1 ? '1 riga' : `${row.itemCount} righe`}
                  </span>
                  <Badge variant={STATUS_BADGE[row.status].variant}>
                    {STATUS_BADGE[row.status].label}
                  </Badge>
                  {row.status === 'placed' ? (
                    <span
                      className={
                        expiring
                          ? 'text-danger-content text-xs font-medium'
                          : 'text-muted-foreground text-xs'
                      }
                    >
                      <Clock className="mr-1 inline size-3" />
                      {left < 0
                        ? 'scaduto'
                        : left === 0
                          ? 'scade oggi'
                          : left === 1
                            ? 'scade domani'
                            : `scade fra ${left} giorni`}
                    </span>
                  ) : (
                    <span className="text-muted-foreground text-xs">
                      ordinato il {formatDateTime(row.placedAt)}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setOpenOrderId((current) => (current === row.id ? null : row.id))
                    }}
                  >
                    {openOrderId === row.id ? 'Chiudi' : 'Apri'}
                  </Button>
                </div>
                {openOrderId === row.id ? (
                  <OrderDetail
                    orderId={row.id}
                    canWrite={canWrite}
                    onDone={() => {
                      setOpenOrderId(null)
                    }}
                  />
                ) : null}
              </div>
            )
          })}
        </div>
      )}
    </>
  )
}

/**
 * Il dettaglio, che si apre e non si riassume.
 *
 * Si carica **quando si apre** e non per ogni riga: caricare le righe di cinquanta ordini sarebbero
 * cinquanta richieste per una tabella che di norma si guarda e basta. È la scelta del registro di
 * magazzino (INV-05), e per la stessa ragione.
 */
function OrderDetail({
  orderId,
  canWrite,
  onDone,
}: {
  orderId: Uuid
  canWrite: boolean
  onDone: () => void
}) {
  const detail = useShopOrder(orderId)
  const locations = useLocations()
  const till = useCurrentCashSession()
  const { collect, cancel } = useShopOrderMutations()
  const [locationId, setLocationId] = useState<Uuid | null>(null)
  const [cash, setCash] = useState(true)
  const [reason, setReason] = useState('')
  const [cancelling, setCancelling] = useState(false)

  if (detail.isPending) return <Skeleton className="m-3 h-24" />
  if (detail.isError || detail.data === undefined) {
    return (
      <div className="p-3">
        <ErrorState
          message={errorMessage(detail.error)}
          onRetry={() => {
            void detail.refetch()
          }}
        />
      </div>
    )
  }

  const order = detail.data
  const canCollect = canWrite && order.status === 'placed'
  // Un incasso in contanti pretende la cassa aperta di quella sede (PAY-05). Il server lo rifiuta
  // comunque; dirlo qui evita di scoprirlo con la persona davanti.
  const tillOpen = till.data != null

  return (
    <div className="bg-muted/40 border-border border-t p-3">
      <div className="mb-3 grid gap-1 text-sm sm:grid-cols-2">
        <div>
          <span className="text-muted-foreground">Ordinato:</span> {formatDateTime(order.placedAt)}
        </div>
        <div>
          <span className="text-muted-foreground">Ritirabile entro:</span>{' '}
          {formatDate(order.expiresOn)}
        </div>
        {order.customerEmail === null ? null : (
          <div>
            <span className="text-muted-foreground">Email:</span> {order.customerEmail}
          </div>
        )}
        {order.collectedAt === null ? null : (
          <div>
            <span className="text-muted-foreground">Ritirato:</span>{' '}
            {formatDateTime(order.collectedAt)}
          </div>
        )}
        {order.cancelReason === null ? null : (
          <div className="sm:col-span-2">
            <span className="text-muted-foreground">Annullato perché:</span> {order.cancelReason}
          </div>
        )}
      </div>

      {order.note === null ? null : (
        <Alert variant="info" className="mb-3">
          Messaggio del cliente: {order.note}
        </Alert>
      )}

      <table className="mb-3 w-full text-sm">
        <tbody className="divide-border divide-y">
          {order.items.map((item) => (
            <tr key={item.position}>
              <td className="py-1">{item.name}</td>
              <td className="py-1 text-right tabular-nums">×{item.quantity}</td>
              <td className="py-1 text-right tabular-nums">
                {formatMoney(item.unitPriceCents, order.currency)}
              </td>
              <td className="py-1 text-right font-medium tabular-nums">
                {formatMoney(item.lineTotalCents, order.currency)}
              </td>
            </tr>
          ))}
          <tr>
            <td className="pt-2 font-medium" colSpan={3}>
              Totale
            </td>
            <td className="pt-2 text-right font-semibold tabular-nums">
              {formatMoney(order.totalCents, order.currency)}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Il prezzo è quello dell'ordine: dirlo qui è ciò che evita la domanda al banco. */}
      <p className="text-muted-foreground mb-3 text-xs">
        I prezzi sono quelli del giorno dell’ordine e non si modificano da qui. Per cambiarli,
        annulla l’ordine e registra una vendita al banco.
      </p>

      {canCollect ? (
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <span className="text-muted-foreground mb-1 block text-xs">
              Sede da cui esce la merce
            </span>
            <select
              value={locationId ?? ''}
              onChange={(event) => {
                setLocationId(event.target.value === '' ? null : event.target.value)
              }}
              className="border-border rounded-md border px-2 py-1 text-sm"
            >
              <option value="">Nessuna (il magazzino non si muove)</option>
              {(locations.data ?? []).map((location) => (
                <option key={location.id} value={location.id}>
                  {location.name}
                </option>
              ))}
            </select>
          </label>
          <Checkbox
            className="text-sm"
            checked={cash}
            onCheckedChange={setCash}
            label="Incassa in contanti"
          />
          <Button
            variant="primary"
            size="sm"
            disabled={collect.isPending || (cash && !tillOpen)}
            onClick={() => {
              collect.mutate(
                {
                  orderId,
                  input: {
                    ...(locationId === null ? {} : { locationId }),
                    ...(cash
                      ? { payments: [{ method: 'cash', amountCents: order.totalCents }] }
                      : {}),
                  },
                },
                {
                  onSuccess: () => {
                    notify({ message: `Ordine ${order.code} consegnato.`, variant: 'success' })
                    onDone()
                  },
                  onError: (error) => {
                    notify({ message: errorMessage(error), variant: 'danger' })
                  },
                },
              )
            }}
          >
            <PackageCheck className="size-4" /> Ritira e vendi
          </Button>
          {cash && !tillOpen ? (
            <Alert variant="warning" className="w-full">
              Per incassare in contanti serve la cassa aperta. Aprila da «Vendite», oppure togli la
              spunta e incassa dopo.
            </Alert>
          ) : null}

          {cancelling ? (
            <div className="flex w-full flex-wrap items-end gap-2">
              <label className="flex-1 text-sm">
                <span className="text-muted-foreground mb-1 block text-xs">
                  Perché non si consegna
                </span>
                <input
                  value={reason}
                  onChange={(event) => {
                    setReason(event.target.value)
                  }}
                  placeholder="Non disponibile, ritirato altrove, doppione…"
                  className="border-border w-full rounded-md border px-2 py-1 text-sm"
                />
              </label>
              <Button
                variant="danger"
                size="sm"
                disabled={reason.trim() === '' || cancel.isPending}
                onClick={() => {
                  cancel.mutate(
                    { orderId, reason: reason.trim() },
                    {
                      onSuccess: () => {
                        notify({ message: `Ordine ${order.code} annullato.`, variant: 'success' })
                        onDone()
                      },
                      onError: (error) => {
                        notify({ message: errorMessage(error), variant: 'danger' })
                      },
                    },
                  )
                }}
              >
                Conferma annullamento
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCancelling(false)
                  setReason('')
                }}
              >
                Torna indietro
              </Button>
            </div>
          ) : (
            <Button
              variant="danger-outline"
              size="sm"
              onClick={() => {
                setCancelling(true)
              }}
            >
              <Ban className="size-4" /> Annulla ordine
            </Button>
          )}
        </div>
      ) : null}
    </div>
  )
}
